/**
 * 카테고리 낱말 → 카테고리 코드 (10-19).
 *
 * 검색어가 "상품 이름"이 아니라 "카테고리 이름"인 경우가 있다 — "렌터카", "바이크",
 * "レンタカー", "租车". 상품 글자 검색(PGroonga)은 제목·검색 키워드에 그 낱말이 있어야
 * 잡는데, 렌터카 상품 제목은 "토요타 알파드" 같은 차종 이름뿐이라 "렌터카"로는 0건이었다
 * (키워드 스코프만 업체명 "미야코지마 에어 렌터카" 덕에 우연히 걸렸고, 지명을 고르면
 * bounds RPC 가 업체명을 안 봐서 0건). 오토바이는 글자로 잡히지만 "bike"에 전기자전거가
 * 섞이고, 오토바이 전용 화면(지점 허브)이 아니라 상품 카드 목록으로 떨어졌다.
 *
 * 그래서 검색바 제출(composer)과 검색 페이지 진입(page.tsx)에서 **검색어 전체가 카테고리
 * 낱말이면** 검색어 대신 카테고리 필터로 보낸다 — 카테고리 랜딩과 같은 화면이 된다.
 * DB 함수는 건드리지 않는다.
 *
 * "bike"는 언어마다 뜻이 다르다 — 한국어 "바이크"·일본어 "バイク"는 오토바이, 영어 "bike"는
 * 자전거. 그래서 뜻이 갈리는 낱말만 화면 언어별 사전(LOCALE_WORDS)에 두고, 나머지는 공용
 * 사전(CATEGORY_WORDS)에 둔다. 같은 낱말이 화면 언어에 따라 다른 카테고리로 간다.
 *
 * 사전은 손으로 고른 낱말만 둔다. 상품 글자 검색이 이미 정확한 낱말(한복·기모노·유모차·
 * 카메라)은 넣지 않는다 — 한복을 전통의상(10-05-01)으로 보내면 기모노가 섞인다.
 * 화면 문구(SEO 랜딩)의 낱말 묶음은 lib/seo/landing-wording.ts (motorcycle·car·camping).
 */

import { CAR_CATEGORY_CODE } from "@/lib/mobility/car-rental";
import {
  CAMPING_CATEGORY_CODE,
  MOBILITY_CATEGORY_CODE,
} from "@/lib/mobility/station-hub";
import { normalizeCategoryCodes } from "@/config/categories";

/** 전기자전거 / 자전거 */
export const BICYCLE_CATEGORY_CODE = "50-01-03";
/** 전동 킥보드 (10-19 prod 재고 0 — 그래도 맞는 화면으로 보낸다) */
export const KICKBOARD_CATEGORY_CODE = "50-01-04";
/** 캠핑카 (10-19 prod 재고 0) */
export const CAMPERVAN_CATEGORY_CODE = "50-03-01";
/** 스마트폰 중분류 */
export const SMARTPHONE_CATEGORY_CODE = "20-01";
/** 텐트 세트 */
export const TENT_CATEGORY_CODE = "10-04-02";
/** 차박 세트 */
export const CAR_CAMPING_CATEGORY_CODE = "10-04-03";
/** 비치 텐트 / 그늘막 */
export const BEACH_TENT_CATEGORY_CODE = "10-02-03";
/** 카시트 */
export const CAR_SEAT_CATEGORY_CODE = "40-01-02";

/**
 * 코드별 낱말(모든 화면 언어 공용). 비교는 소문자·NFKC·공백/기호 제거(compact) 후 완전 일치 — "렌트 카",
 * "e-bike", "rent a car" 도 같은 낱말로 본다. 여러 낱말 묶음("car seat")이 한 낱말("car")
 * 보다 먼저 맞는다.
 */
const CATEGORY_WORDS: Readonly<Record<string, readonly string[]>> = {
  // 오토바이·스쿠터 — 스쿠터 상품은 전부 원付·125cc 오토바이(킥보드 재고 0)라 여기로 보낸다.
  [MOBILITY_CATEGORY_CODE]: [
    "오토바이", "바이크", "모터바이크", "모토바이크", "스쿠터", "원동기", "렌탈바이크", "렌트바이크",
    "motorbike", "motorbikes", "motorcycle", "motorcycles", "moto",
    "scooter", "scooters", "motorscooter", "moped", "mopeds",
    "バイク", "オートバイ", "レンタルバイク", "スクーター", "原付", "原付バイク", "原チャリ",
    "摩托车", "摩托車", "摩托", "机车", "機車", "重机", "重機", "电单车", "電單車",
    "踏板车", "踏板車", "速克达", "速克達", "租摩托车", "租摩托車", "租机车", "租機車",
  ],
  [BICYCLE_CATEGORY_CODE]: [
    "자전거", "전기자전거", "전동자전거",
    "bicycle", "bicycles", "ebike", "ebikes", "electricbike", "electricbikes", "cycle", "cycling",
    "自転車", "電動自転車", "電動アシスト自転車", "レンタサイクル", "サイクル",
    "自行车", "自行車", "电动自行车", "電動自行車", "单车", "單車", "脚踏车", "腳踏車",
  ],
  [KICKBOARD_CATEGORY_CODE]: [
    "킥보드", "전동킥보드", "킥스쿠터",
    "kickboard", "kickscooter", "escooter", "electricscooter",
    "キックボード", "電動キックボード",
    "滑板车", "滑板車", "电动滑板车", "電動滑板車",
  ],
  [CAR_CATEGORY_CODE]: [
    "렌터카", "렌트카", "렌터카대여", "자동차", "승용차", "자동차렌트", "자동차렌탈", "차량렌트", "차렌트",
    "car", "cars", "rentacar", "carrental", "rentalcar", "carhire", "hirecar",
    "レンタカー", "自動車", "車", "クルマ",
    "租车", "租車", "汽车", "汽車", "汽车租赁", "汽車租賃", "租赁汽车", "租賃汽車",
  ],
  [CAMPERVAN_CATEGORY_CODE]: [
    "캠핑카", "캠퍼밴",
    "campervan", "campervans", "campingcar", "camper", "motorhome", "rv",
    "キャンピングカー",
    "露营车", "露營車", "房车", "房車",
  ],
  [SMARTPHONE_CATEGORY_CODE]: [
    "스마트폰", "휴대폰", "핸드폰", "휴대전화", "폰",
    "smartphone", "smartphones", "phone", "phones", "mobilephone", "cellphone",
    "スマホ", "スマートフォン", "携帯", "携帯電話",
    "手机", "手機", "智能手机", "智慧型手機",
  ],
  [CAMPING_CATEGORY_CODE]: [
    "캠핑", "캠핑장비", "캠핑용품", "글램핑",
    "camping", "campinggear", "glamping",
    "キャンプ", "キャンプ用品", "キャンプ道具",
    "露营", "露營", "露营装备", "露營裝備",
  ],
  [TENT_CATEGORY_CODE]: ["텐트", "tent", "tents", "テント", "帐篷", "帳篷"],
  [CAR_CAMPING_CATEGORY_CODE]: ["차박", "carcamping", "車中泊", "车中泊"],
  [BEACH_TENT_CATEGORY_CODE]: ["비치텐트", "그늘막", "beachtent", "ビーチテント", "沙滩帐篷", "沙灘帳篷"],
  [CAR_SEAT_CATEGORY_CODE]: ["카시트", "carseat", "carseats", "チャイルドシート", "儿童座椅", "兒童座椅", "安全座椅"],
};

type WordLocale = "ko" | "en" | "ja" | "zh";

/**
 * 화면 언어마다 뜻이 갈리는 낱말 — 공용 사전에 없고 여기에만 있다.
 *  - ko·ja: "bike"는 오토바이(바이크·バイク와 같은 뜻으로 쓴다 — ja 화면의 영어 "bike"도
 *    バイク로 읽는다).
 *  - en: "bike"는 자전거("bike rental" = 자전거 대여, 오토바이는 motorbike·motorcycle).
 *  - zh: "bike"는 영어 화면과 같이 자전거(중국어 화면 문구는 摩托车·機車, 자전거는 自行车·單車 —
 *    중국어권 사용자가 영어로 치는 "bike"는 자전거다).
 */
const LOCALE_WORDS: Readonly<Record<WordLocale, Readonly<Record<string, readonly string[]>>>> = {
  ko: { [MOBILITY_CATEGORY_CODE]: ["bike", "bikes"] },
  ja: { [MOBILITY_CATEGORY_CODE]: ["bike", "bikes"] },
  en: { [BICYCLE_CATEGORY_CODE]: ["bike", "bikes"] },
  zh: { [BICYCLE_CATEGORY_CODE]: ["bike", "bikes"] },
};

/** 화면 언어 → 사전 언어. 모르는 언어(새로 열 언어)는 영어 사전. */
function wordLocaleOf(locale: string | null | undefined): WordLocale {
  if (locale === "ko" || locale === "ja" || locale === "en") return locale;
  if (locale?.startsWith("zh")) return "zh";
  return "en";
}

/**
 * 대여를 뜻하는 군말 — 카테고리 낱말 옆에 붙어도 뜻이 같다("오토바이 렌트", "rent a car",
 * "テント レンタル"). 이것만 남으면("렌탈") 카테고리가 아니다.
 */
const GENERIC_WORDS: ReadonlySet<string> = new Set(
  [
    "대여", "렌탈", "렌트", "빌리기", "빌려", "대여점", "장비", "용품", "세트",
    "rent", "rental", "rentals", "renting", "hire", "a", "for", "gear", "equipment", "set",
    "レンタル", "貸出", "貸し出し", "用品", "道具", "セット",
    "租", "租赁", "租賃", "出租", "租借", "装备", "裝備", "设备", "設備",
  ].map(compact),
);

/**
 * 검색어 나머지(카테고리 낱말을 뺀 글자)와 **같이** 카테고리로 보내도 되는 코드.
 * 상품 제목에 카테고리 낱말이 없는 렌터카만("토요타 렌터카" → 토요타 + 자동차, 지명 사전에
 * 아직 없는 "미야코지마 렌터카" → 미야코지마 + 자동차 — 업체명으로 잡힌다). 나머지 카테고리는
 * 글자 검색이 이미 그 낱말을 찾으므로 손대지 않는다("폰 케이스"를 스마트폰으로 좁히면 안 된다). 오토바이는 특히 안 된다 — 지점 허브 화면은 검색어를
 * 쓰지 않아 "혼다 바이크"가 오토바이 전체가 된다.
 */
const COMBINABLE_CODES: ReadonlySet<string> = new Set([CAR_CATEGORY_CODE]);

function compact(s: string): string {
  return s.normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
}

function buildWordMap(locale: WordLocale): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const table of [CATEGORY_WORDS, LOCALE_WORDS[locale]]) {
    for (const [code, words] of Object.entries(table)) {
      for (const word of words) {
        const key = compact(word);
        const prev = map.get(key);
        if (prev && prev !== code) {
          throw new Error(`category word "${word}" maps to both ${prev} and ${code} (${locale})`);
        }
        map.set(key, code);
      }
    }
  }
  return map;
}

const WORD_MAPS: Readonly<Record<WordLocale, ReadonlyMap<string, string>>> = {
  ko: buildWordMap("ko"),
  en: buildWordMap("en"),
  ja: buildWordMap("ja"),
  zh: buildWordMap("zh"),
};

export interface CategoryWordMatch {
  /** 카테고리 코드 */
  code: string;
  /** 카테고리 낱말·군말을 뺀 나머지 검색어(원문 토큰, 공백 구분). 없으면 "" */
  rest: string;
}

/**
 * 검색어에서 카테고리 낱말을 찾는다(`locale` = 화면 언어 — "bike"의 뜻이 갈린다).
 *  1) 검색어 전체("렌트 카", "rent a car") 2) 군말을 뺀 전체("오토바이 렌트")
 *  3) 토큰 하나(또는 붙은 두 토큰)가 카테고리 낱말 — 나머지를 rest 로. 서로 다른
 *     카테고리 낱말이 둘 이상이면 판단하지 않는다(null).
 */
export function matchCategoryWord(
  query: string | null | undefined,
  locale: string,
): CategoryWordMatch | null {
  const WORD_TO_CODE = WORD_MAPS[wordLocaleOf(locale)];
  const tokens = (query ?? "").trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;

  const whole = WORD_TO_CODE.get(compact(tokens.join("")));
  if (whole) return { code: whole, rest: "" };

  const content = tokens.filter((t) => !GENERIC_WORDS.has(compact(t)));
  if (content.length === 0) return null;
  const core = WORD_TO_CODE.get(compact(content.join("")));
  if (core) return { code: core, rest: "" };

  let found: { code: string; start: number; end: number } | null = null;
  for (let i = 0; i < content.length; i++) {
    const pair = i + 1 < content.length ? WORD_TO_CODE.get(compact(content[i] + content[i + 1])) : undefined;
    const single = WORD_TO_CODE.get(compact(content[i]));
    const code = pair ?? single;
    if (!code) continue;
    if (found && found.code !== code) return null;
    found = found ?? { code, start: i, end: pair ? i + 2 : i + 1 };
    if (pair) i++;
  }
  if (!found) return null;
  const rest = [...content.slice(0, found.start), ...content.slice(found.end)]
    .filter((t) => !WORD_TO_CODE.has(compact(t)))
    .join(" ");
  return { code: found.code, rest };
}

export interface CategoryWordRoute {
  /** 남길 검색어(없으면 undefined) */
  q: string | undefined;
  /** 카테고리 필터(기존 선택 + 낱말 카테고리, 정규화) */
  categories: string[];
  /** 낱말로 새로 붙은 카테고리 코드 */
  added: string;
}

/**
 * 검색어 → (검색어, 카테고리) 라우팅 규칙 — 검색바 제출·검색 페이지 진입·검색바 추천이 같이
 * 쓴다(셋 다 같은 화면 언어를 넘겨야 같은 낱말이 같은 곳으로 간다).
 * 검색어 전체가 카테고리 낱말이면 검색어를 비우고 카테고리를 붙인다. 나머지가 있으면
 * COMBINABLE_CODES 만 "나머지 검색어 + 카테고리"로, 그 밖은 그대로 둔다(null).
 */
export function routeCategoryWord(
  q: string | null | undefined,
  categories: readonly string[],
  locale: string,
): CategoryWordRoute | null {
  const match = matchCategoryWord(q, locale);
  if (!match) return null;
  if (match.rest && !COMBINABLE_CODES.has(match.code)) return null;
  return {
    q: match.rest || undefined,
    categories: normalizeCategoryCodes([...categories, match.code]),
    added: match.code,
  };
}

type SearchParamsRecord = { [key: string]: string | string[] | undefined };

/** 이 스코프에서는 검색어를 카테고리로 바꾸지 않는다 — 파트너 가게 안·내 주변은 허브 화면이 없다. */
const NO_CATEGORY_WORD_SCOPES: ReadonlySet<string> = new Set(["partner", "nearby"]);

/**
 * 검색 페이지 주소의 검색어가 카테고리 낱말이면 카테고리 필터 주소로(검색 페이지 진입용).
 * 검색바는 제출할 때 이미 바꿔 보내므로(composer) 여기 걸리는 건 옛 최근 검색 칩·외부 링크·
 * 구글 사이트링크 검색창(`?q={search_term_string}`)이다. 다른 파라미터는 순서 그대로 둔다.
 */
export function categoryWordRedirect(
  locale: string,
  decodedRegion: string,
  search: SearchParamsRecord,
): string | null {
  const str = (v: string | string[] | undefined) => (typeof v === "string" ? v : undefined);
  const scope = str(search.scope);
  if (scope && NO_CATEGORY_WORD_SCOPES.has(scope)) return null;
  const q = str(search.q) ?? str(search.search);
  if (!q) return null;
  const current = (str(search.categories) ?? "")
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
  const routed = routeCategoryWord(q, current, locale);
  if (!routed) return null;

  const rp = new URLSearchParams();
  let categoriesWritten = false;
  for (const [key, value] of Object.entries(search)) {
    if (key === "search") continue;
    if (key === "q") {
      if (routed.q) rp.append("q", routed.q);
      continue;
    }
    if (key === "categories") {
      rp.append("categories", routed.categories.join(","));
      categoriesWritten = true;
      continue;
    }
    for (const v of Array.isArray(value) ? value : value === undefined ? [] : [value]) {
      rp.append(key, v);
    }
  }
  if (!str(search.q) && routed.q) rp.append("q", routed.q);
  if (!categoriesWritten) rp.append("categories", routed.categories.join(","));
  return `/${locale}/s/${encodeURIComponent(decodedRegion)}?${rp.toString()}`;
}
